import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LangContext";
import PageMeta from "@/components/PageMeta";
import { Layers, Factory, Boxes, Building2, ShieldCheck, Leaf, Volume2, Clock, ArrowRight, Play } from "lucide-react";

const STEP_IMAGES = [
  "/images/prod-step-bim.jpg",
  "/images/prod-step-produktion.jpg",
  "/images/prod-step-bau.jpg",
  "/images/prod-step-fertig.jpg",
];

const VIDEO_IDS = ["bCB9cDZIGoY", "25eVqSLXfTE"];
const FEATURED_VIDEO_ID = "Smr0CamTNjA";
const FACADE_IMAGES = ["/images/facade-ziegel.jpg", "/images/facade-stein.jpg", "/images/facade-putz.jpg", "/images/facade-stuck.jpg", "/images/facade-ornament.jpg", "/images/facade-saeule.jpg", "/images/facade-detail.jpg", "/images/facade-gesims.jpg", "/images/facade-projekt.jpg", "/images/facade-arched.jpg", "/images/facade-grc-halle.jpg"];

const CONTENT = {
  de: {
    eyebrow: "Eigene Produktion · A+++",
    titleL1: "Produktion",
    titleL2: "A+++",
    lede: "Von der digitalen Planung bis zur schlüsselfertigen Übergabe realisiert Royalhouse Wohnbauten mit eigener, industrieller Fertigteilproduktion. Massivbauweise vollständig aus Beton – erdbebensicher, hoch energieeffizient und im Werk millimetergenau vorgefertigt.",
    facts: [
      { value: "72.000 m²", label: "Produktionsfläche" },
      { value: "~30", label: "Wohnungen / Woche" },
      { value: "~12", label: "Geschosse / Monat" },
      { value: "9,5", label: "Erdbebensicher bis Magnitude" },
      { value: "A+++", label: "Energieklasse" },
    ],
    stepsTitle: "Vier Schritte – ein durchgängiger, industrieller Prozess.",
    steps: [
      { no: "01", icon: Layers, title: "BIM-Planung", text: "Architektur, Tragwerk und Gebäudetechnik werden in einem einzigen, koordinierten BIM-Modell zusammengeführt. Kollisionsprüfung, Massenermittlung und werkstattreife Fertigungspläne werden unmittelbar daraus abgeleitet.", aplus: "Planungssicherheit von der ersten Linie an – Konflikte werden im Modell gelöst, nicht auf der Baustelle." },
      { no: "02", icon: Factory, title: "Produktion (GRC-Fertigteile)", text: "Wände, Decken und Fassaden entstehen witterungsunabhängig im Werk auf einer der leistungsstärksten GRC-Anlagen Europas. Elektro-, Lüftungs- und Dämmebenen werden bereits werkseitig integriert und dokumentiert.", aplus: "Reproduzierbare Werksqualität – jedes Bauteil geprüft, vermessen und QR-rückverfolgbar." },
      { no: "03", icon: Boxes, title: "Bau & Montage", text: "Die vorgefertigten Elemente werden im Taktprinzip per Kran montiert – witterungs- und tageszeitunabhängig. Das Ergebnis sind passgenaue, perfekt glatte Wände mit minimalem Nacharbeitsaufwand.", aplus: "Bis zu 40 % kürzere Bauzeit gegenüber konventioneller Massivbauweise." },
      { no: "04", icon: Building2, title: "Fertigstellung", text: "Innenausbau, Einregulierung der Gebäudetechnik (Test & Balance), digitales Prüfbuch sowie schlüsselfertige Übergabe inklusive As-Built-Modell und digitalem Zwilling für den Betrieb.", aplus: "Termingerechte Übergabe mit lückenloser Dokumentation." },
    ],
    wall: {
      eyebrow: "Wandaufbau · Alles Beton",
      title: "Erdbebensicher bis Magnitude 9,5.",
      text: "Unsere mehrschichtige Sandwichwand vereint Tragfähigkeit, Wärmedämmung und Fassade in einem einzigen, werkseitig gefertigten Bauteil. Der massive, stahlbewehrte Betonkern macht die Häuser außergewöhnlich robust — ausgelegt für höchste seismische Anforderungen und eine Lebensdauer über Generationen.",
      layers: [
        { name: "GRC-Außenschale", desc: "Glasfaserbeton — dünn, aber hochfest, UV- und witterungsbeständig. Frei formbar in nahezu jeder Textur, Farbe und Oberfläche, von Ziegel bis Naturstein." },
        { name: "Mineralwoll-Dämmung", desc: "Nichtbrennbare Hochleistungs-Kerndämmung — sorgt für Energieklasse A+++, exzellenten Schallschutz und sommerlichen Hitzeschutz." },
        { name: "Tragbeton-Kern", desc: "Massiver, stahlbewehrter Beton — übernimmt die gesamte Statik und macht die Wand monolithisch und erdbebensicher." },
      ],
      benefits: [
        { icon: ShieldCheck, text: "Massiver Beton — erdbebensicher bis Magnitude 9,5" },
        { icon: Leaf, text: "Energieklasse A+++ durch integrierte Kerndämmung" },
        { icon: Volume2, text: "Hervorragender Schallschutz" },
        { icon: Clock, text: "Werkseitig integriert — schnelle, saubere Montage" },
      ],
    },
    videos: {
      eyebrow: "Aus dem Werk",
      title: "Unsere Produktion im Film.",
      text: "Ein Blick in die industrielle Fertigung der GRC-Fertigteile — von der Form über die Bewehrung bis zur Montage auf der Baustelle.",
      featured: { badge: "Royal House Bauträger · Imagefilm", title: "Vom Grundstück zum fertigen Haus.", caption: "Der komplette, öko-moderne Bauprozess von Royal House Bauträger — von der leeren Baustelle über die Fertigteil-Montage bis zum bezugsfertigen Gebäude." },
      captions: ["GRC-Fertigteile · Herstellung & Veredelung", "Industrielle Vorfertigung · Werk & Montage"],
    },
    facades: {
      eyebrow: "GRC-Oberflächen",
      title: "Eine Fassade, viele Gesichter.",
      text: "Die GRC-Außenschale lässt sich in nahezu jeder Textur und Farbe fertigen — von klassischer Ziegel- und Natursteinoptik über feinen Putz bis zu Gesimsen, Stuck-Ornamenten, Konsolen und Säulen.",
      items: ["Ziegeloptik", "Natursteinoptik", "Putzoptik", "Zierprofile & Gesimse", "Stuck-Ornamente", "Konsolen & Säulen", "Fassaden-Detail", "Gesims & Kranz", "Realisiertes Projekt", "Bogenfassade", "U-Bahn · Faserbeton"],
    },
    cta: { title: "Ihr Projekt, industriell gefertigt.", text: "Sprechen Sie mit uns über Ihr Bauvorhaben — wir zeigen Ihnen den kompletten Weg von BIM bis Übergabe.", button: "Kontakt aufnehmen" },
  },
  en: {
    eyebrow: "In-house production · A+++",
    titleL1: "Production",
    titleL2: "A+++",
    lede: "From digital planning to turnkey handover, Royalhouse delivers residential buildings from its own industrial precast production. Solid construction entirely in concrete — earthquake-safe, highly energy-efficient and prefabricated to the millimetre in the factory.",
    facts: [
      { value: "72,000 m²", label: "Production floor space" },
      { value: "~30", label: "Apartments / week" },
      { value: "~12", label: "Floors / month" },
      { value: "9.5", label: "Earthquake-safe up to magnitude" },
      { value: "A+++", label: "Energy class" },
    ],
    stepsTitle: "Four steps – one seamless, industrial process.",
    steps: [
      { no: "01", icon: Layers, title: "BIM planning", text: "Architecture, structure and building services are consolidated into a single, coordinated BIM model. Clash detection, quantity take-off and workshop-ready fabrication drawings are derived directly from it.", aplus: "Planning certainty from the very first line — conflicts are resolved in the model, not on site." },
      { no: "02", icon: Factory, title: "Production (GRC precast)", text: "Walls, slabs and façades are produced weather-independently in the factory on one of Europe's most powerful GRC lines. Electrical, ventilation and insulation layers are integrated and documented at the works.", aplus: "Reproducible factory quality — every element inspected, measured and QR-traceable." },
      { no: "03", icon: Boxes, title: "Build & assembly", text: "The prefabricated elements are craned into place in takt — independent of weather or time of day. The result: precise, perfectly smooth walls with minimal rework.", aplus: "Up to 40% shorter construction time than conventional solid construction." },
      { no: "04", icon: Building2, title: "Completion", text: "Interior fit-out, commissioning of the building services (test & balance), a digital inspection log and turnkey handover including as-built model and digital twin for operation.", aplus: "On-schedule handover with complete documentation." },
    ],
    wall: {
      eyebrow: "Wall build-up · All concrete",
      title: "Earthquake-safe up to magnitude 9.5.",
      text: "Our multi-layer sandwich wall combines load-bearing capacity, thermal insulation and façade in a single factory-made component. The solid, steel-reinforced concrete core makes the buildings exceptionally robust — designed for the highest seismic demands and a lifespan across generations.",
      layers: [
        { name: "GRC outer shell", desc: "Glass-fibre reinforced concrete — thin yet high-strength, UV- and weatherproof. Freely shapeable in almost any texture, colour and finish, from brick to natural stone." },
        { name: "Mineral-wool insulation", desc: "Non-combustible high-performance core insulation — delivers energy class A+++, excellent acoustics and summer heat protection." },
        { name: "Structural concrete core", desc: "Solid, steel-reinforced concrete — carries the entire structure and makes the wall monolithic and earthquake-safe." },
      ],
      benefits: [
        { icon: ShieldCheck, text: "Solid concrete — earthquake-safe up to magnitude 9.5" },
        { icon: Leaf, text: "Energy class A+++ through integrated core insulation" },
        { icon: Volume2, text: "Excellent acoustic protection" },
        { icon: Clock, text: "Factory-integrated — fast, clean assembly" },
      ],
    },
    videos: {
      eyebrow: "From the factory",
      title: "Our production on film.",
      text: "A look inside the industrial manufacturing of GRC precast elements — from mould and reinforcement to on-site assembly.",
      featured: { badge: "Royal House Bauträger · Image film", title: "From plot to finished home.", caption: "The complete, eco-modern building process of Royal House Bauträger — from the empty site through precast assembly to the move-in-ready building." },
      captions: ["GRC precast · manufacturing & finishing", "Industrial prefabrication · factory & assembly"],
    },
    facades: {
      eyebrow: "GRC surfaces",
      title: "One facade, many faces.",
      text: "The GRC outer shell can be produced in almost any texture and colour — from classic brick and natural-stone looks and fine plaster to cornices, stucco ornaments, corbels and columns.",
      items: ["Brick look", "Natural stone", "Plaster finish", "Cornices & profiles", "Stucco ornaments", "Corbels & columns", "Façade detail", "Cornice & crown", "Completed project", "Arched façade", "Metro · GRC"],
    },
    cta: { title: "Your project, industrially built.", text: "Talk to us about your development — we'll show you the full path from BIM to handover.", button: "Get in touch" },
  },
  ru: {
    eyebrow: "Собственное производство · A+++",
    titleL1: "Производство",
    titleL2: "A+++",
    lede: "От цифрового проектирования до сдачи под ключ Royalhouse реализует жилые здания на собственном промышленном производстве сборных элементов. Монолитно-массивная конструкция полностью из бетона — сейсмостойкая, высокоэнергоэффективная и изготовленная на заводе с точностью до миллиметра.",
    facts: [
      { value: "72 000 м²", label: "Производственная площадь" },
      { value: "~30", label: "Квартир / неделя" },
      { value: "~12", label: "Этажей / месяц" },
      { value: "9,5", label: "Сейсмостойкость до магнитуды" },
      { value: "A+++", label: "Класс энергоэффективности" },
    ],
    stepsTitle: "Четыре шага — единый сквозной промышленный процесс.",
    steps: [
      { no: "01", icon: Layers, title: "BIM-проектирование", text: "Архитектура, несущие конструкции и инженерные системы объединяются в единой согласованной BIM-модели. Проверка коллизий, подсчёт объёмов и готовые к производству чертежи формируются непосредственно из неё.", aplus: "Уверенность в проекте с первой линии — конфликты решаются в модели, а не на стройплощадке." },
      { no: "02", icon: Factory, title: "Производство (GRC-элементы)", text: "Стены, перекрытия и фасады изготавливаются независимо от погоды на одной из самых мощных GRC-линий Европы. Электрика, вентиляция и слои утепления интегрируются и документируются уже на заводе.", aplus: "Воспроизводимое заводское качество — каждый элемент проверен, обмерен и прослеживается по QR-коду." },
      { no: "03", icon: Boxes, title: "Строительство и монтаж", text: "Сборные элементы монтируются краном по тактовому принципу — независимо от погоды и времени суток. Результат — точные, идеально гладкие стены с минимальной доработкой.", aplus: "До 40% меньше срок строительства по сравнению с традиционной монолитной технологией." },
      { no: "04", icon: Building2, title: "Сдача под ключ", text: "Внутренняя отделка, пусконаладка инженерных систем (test & balance), цифровой журнал приёмки, а также сдача под ключ вместе с исполнительной as-built моделью и цифровым двойником для эксплуатации.", aplus: "Сдача точно в срок с полной документацией." },
    ],
    wall: {
      eyebrow: "Состав стены · Всё из бетона",
      title: "Сейсмостойкость до магнитуды 9,5.",
      text: "Наша многослойная сэндвич-стена объединяет несущую способность, теплоизоляцию и фасад в одном заводском элементе. Массивный армированный бетонный сердечник делает дома исключительно прочными — рассчитаны на высочайшие сейсмические нагрузки и срок службы на поколения.",
      layers: [
        { name: "Наружная оболочка GRC", desc: "Стеклофибробетон — тонкий, но высокопрочный, стойкий к УФ и погоде. Свободно формуется в любой текстуре, цвете и отделке — от кирпича до натурального камня." },
        { name: "Минераловатное утепление", desc: "Негорючее высокоэффективное утепление сердечника — обеспечивает класс A+++, отличную акустику и защиту от летней жары." },
        { name: "Несущий бетонный сердечник", desc: "Массивный армированный бетон — несёт всю конструкцию, делая стену монолитной и сейсмостойкой." },
      ],
      benefits: [
        { icon: ShieldCheck, text: "Массивный бетон — сейсмостойкость до магнитуды 9,5" },
        { icon: Leaf, text: "Класс A+++ за счёт интегрированного утепления" },
        { icon: Volume2, text: "Отличная звукоизоляция" },
        { icon: Clock, text: "Заводская интеграция — быстрый и чистый монтаж" },
      ],
    },
    videos: {
      eyebrow: "С завода",
      title: "Наше производство в видео.",
      text: "Взгляд на промышленное изготовление GRC-элементов — от формы и армирования до монтажа на площадке.",
      featured: { badge: "Royal House Bauträger · Имидж-фильм", title: "От участка до готового дома.", caption: "Полный эко-современный процесс строительства Royal House Bauträger — от пустой площадки и монтажа сборных элементов до готового к заселению здания." },
      captions: ["GRC-элементы · изготовление и отделка", "Промышленная префабрикация · завод и монтаж"],
    },
    facades: {
      eyebrow: "GRC-поверхности",
      title: "Один фасад — множество образов.",
      text: "Наружная оболочка GRC изготавливается практически в любой текстуре и цвете — от классической кирпичной и каменной фактуры и тонкой штукатурки до карнизов, лепнины, кронштейнов и колонн.",
      items: ["Кирпич", "Натуральный камень", "Штукатурка", "Карнизы и профили", "Лепнина", "Кронштейны и колонны", "Деталь фасада", "Карниз и венец", "Реализованный проект", "Арочный фасад", "Метро · GRC"],
    },
    cta: { title: "Ваш проект — промышленное производство.", text: "Обсудите с нами ваш проект — покажем весь путь от BIM до сдачи.", button: "Связаться с нами" },
  },
  uk: {
    eyebrow: "Власне виробництво · A+++",
    titleL1: "Виробництво",
    titleL2: "A+++",
    lede: "Від цифрового проєктування до здачі під ключ Royalhouse реалізує житлові будівлі на власному промисловому виробництві збірних елементів. Монолітно-масивна конструкція повністю з бетону — сейсмостійка, високоенергоефективна та виготовлена на заводі з точністю до міліметра.",
    facts: [
      { value: "72 000 м²", label: "Виробнича площа" },
      { value: "~30", label: "Квартир / тиждень" },
      { value: "~12", label: "Поверхів / місяць" },
      { value: "9,5", label: "Сейсмостійкість до магнітуди" },
      { value: "A+++", label: "Клас енергоефективності" },
    ],
    stepsTitle: "Чотири кроки — єдиний наскрізний промисловий процес.",
    steps: [
      { no: "01", icon: Layers, title: "BIM-проєктування", text: "Архітектура, несучі конструкції та інженерні системи об'єднуються в єдиній узгодженій BIM-моделі. Перевірка колізій, підрахунок обсягів і готові до виробництва креслення формуються безпосередньо з неї.", aplus: "Упевненість у проєкті з першої лінії — конфлікти вирішуються в моделі, а не на будівельному майданчику." },
      { no: "02", icon: Factory, title: "Виробництво (GRC-елементи)", text: "Стіни, перекриття та фасади виготовляються незалежно від погоди на одній з найпотужніших GRC-ліній Європи. Електрика, вентиляція та шари утеплення інтегруються й документуються вже на заводі.", aplus: "Відтворювана заводська якість — кожен елемент перевірено, обміряно та відстежується за QR-кодом." },
      { no: "03", icon: Boxes, title: "Будівництво та монтаж", text: "Збірні елементи монтуються краном за тактовим принципом — незалежно від погоди та часу доби. Результат — точні, ідеально гладкі стіни з мінімальним доопрацюванням.", aplus: "До 40% менший строк будівництва порівняно з традиційною монолітною технологією." },
      { no: "04", icon: Building2, title: "Здача під ключ", text: "Внутрішнє оздоблення, пусконалагодження інженерних систем (test & balance), цифровий журнал приймання, а також здача під ключ разом із виконавчою as-built моделлю та цифровим двійником для експлуатації.", aplus: "Здача точно в строк із повною документацією." },
    ],
    wall: {
      eyebrow: "Склад стіни · Усе з бетону",
      title: "Сейсмостійкість до магнітуди 9,5.",
      text: "Наша багатошарова сендвіч-стіна поєднує несучу здатність, теплоізоляцію та фасад в одному заводському елементі. Масивне армоване бетонне ядро робить будинки винятково міцними — розраховані на найвищі сейсмічні навантаження та строк служби на покоління.",
      layers: [
        { name: "Зовнішня оболонка GRC", desc: "Склофібробетон — тонкий, але високоміцний, стійкий до УФ і погоди. Вільно формується в будь-якій текстурі, кольорі та оздобленні — від цегли до натурального каменю." },
        { name: "Мінераловатне утеплення", desc: "Негорюче високоефективне утеплення ядра — забезпечує клас A+++, чудову акустику та захист від літньої спеки." },
        { name: "Несуче бетонне ядро", desc: "Масивний армований бетон — несе всю конструкцію, роблячи стіну монолітною та сейсмостійкою." },
      ],
      benefits: [
        { icon: ShieldCheck, text: "Масивний бетон — сейсмостійкість до магнітуди 9,5" },
        { icon: Leaf, text: "Клас A+++ завдяки інтегрованому утепленню" },
        { icon: Volume2, text: "Чудова звукоізоляція" },
        { icon: Clock, text: "Заводська інтеграція — швидкий і чистий монтаж" },
      ],
    },
    videos: {
      eyebrow: "Із заводу",
      title: "Наше виробництво у відео.",
      text: "Погляд на промислове виготовлення GRC-елементів — від форми та армування до монтажу на майданчику.",
      featured: { badge: "Royal House Bauträger · Іміджевий фільм", title: "Від ділянки до готового дому.", caption: "Повний еко-сучасний процес будівництва Royal House Bauträger — від порожнього майданчика та монтажу збірних елементів до готової до заселення будівлі." },
      captions: ["GRC-елементи · виготовлення та оздоблення", "Промислова префабрикація · завод і монтаж"],
    },
    facades: {
      eyebrow: "GRC-поверхні",
      title: "Один фасад — багато образів.",
      text: "Зовнішня оболонка GRC виготовляється майже в будь-якій текстурі та кольорі — від класичної цегляної та кам'яної фактури й тонкої штукатурки до карнизів, ліпнини, кронштейнів і колон.",
      items: ["Цегла", "Натуральний камінь", "Штукатурка", "Карнизи та профілі", "Ліпнина", "Кронштейни та колони", "Деталь фасаду", "Карниз і вінець", "Реалізований проєкт", "Арковий фасад", "Метро · GRC"],
    },
    cta: { title: "Ваш проєкт — промислове виробництво.", text: "Обговоріть з нами ваш проєкт — покажемо весь шлях від BIM до здачі.", button: "Зв'язатися з нами" },
  },
};

export default function ProduktionPage() {
  const { lang } = useLang();
  const c = CONTENT[lang] || CONTENT.de;

  return (
    <div data-testid="produktion-page" className="pt-20">
      <PageMeta pageKey="produktion" />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <img
          src="/images/prod-hero.jpg"
          alt={c.titleL1}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="produktion-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#051630] via-[#051630]/60 to-[#051630]/20" />
        <div className="relative max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 pb-16 md:pb-24 w-full">
          <div className="flex items-center gap-3 mb-6">
            <p className="text-xs tracking-[0.3em] uppercase text-white/60">{c.eyebrow}</p>
            <span className="inline-flex items-center gap-1.5 border border-emerald-300/40 bg-emerald-400/15 text-emerald-200 px-3 py-1 text-[11px] tracking-[0.15em] uppercase">
              <Leaf size={12} strokeWidth={1.5} /> A+++
            </span>
          </div>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-light uppercase tracking-tighter leading-[0.95]">
            {c.titleL1} <span className="italic">{c.titleL2}</span>
          </h1>
          <p className="mt-8 max-w-3xl text-lg md:text-xl text-white/75 font-light leading-relaxed">
            {c.lede}
          </p>
        </div>
      </section>

      {/* Facts band */}
      <section className="bg-[#0A2244]/50 border-y border-white/10">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-2 md:grid-cols-5 divide-x divide-white/10">
          {c.facts.map((f, i) => (
            <div key={i} className="py-10 px-4 text-center" data-testid={`produktion-fact-${i}`}>
              <p className="font-display text-3xl md:text-4xl font-light text-white">{f.value}</p>
              <p className="mt-2 text-[11px] tracking-[0.2em] uppercase text-white/50 leading-snug">{f.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-tight max-w-3xl mb-16">
          {c.stepsTitle}
        </h2>
        <div className="space-y-16 md:space-y-24">
          {c.steps.map((s, i) => {
            const Icon = s.icon;
            const reverse = i % 2 === 1;
            return (
              <div
                key={s.no}
                className={`grid md:grid-cols-2 gap-8 md:gap-14 items-center ${reverse ? "md:[direction:rtl]" : ""}`}
                data-testid={`produktion-step-${s.no}`}
              >
                <div className="[direction:ltr] relative overflow-hidden aspect-video group bg-black">
                  {s.videoId ? (
                    <iframe
                      className="absolute inset-0 w-full h-full"
                      src={`https://www.youtube-nocookie.com/embed/${s.videoId}?rel=0&modestbranding=1&playsinline=1`}
                      title="Royal House Bauträger"
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      data-testid={`produktion-step-${s.no}-video`}
                    />
                  ) : (
                    <>
                      <img
                        src={STEP_IMAGES[i]}
                        alt={s.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <span className="absolute top-5 left-5 font-display text-5xl text-white/80 drop-shadow-lg">{s.no}</span>
                    </>
                  )}
                </div>
                <div className="[direction:ltr]">
                  <div className="flex items-center gap-3 mb-5">
                    <Icon size={22} strokeWidth={1.5} className="text-emerald-300" />
                    <h3 className="font-display text-2xl md:text-3xl font-light tracking-tight">{s.title}</h3>
                  </div>
                  <p className="text-white/70 font-light leading-relaxed text-lg mb-5">{s.text}</p>
                  <p className="inline-flex items-start gap-2.5 text-emerald-200/90 text-sm border-l-2 border-emerald-300/40 pl-4">
                    <Leaf size={15} strokeWidth={1.5} className="mt-0.5 shrink-0" /> {s.aplus}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Wall build-up */}
      <section className="bg-[#0A2244]/50 border-y border-white/10 py-20 md:py-28" data-testid="produktion-wall-section">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative overflow-hidden border border-white/10 bg-[#051630]">
              <img
                src="/images/wall-section.jpg"
                alt={c.wall.title}
                className="w-full h-full object-cover"
                data-testid="produktion-wall-image"
              />
              <span className="absolute bottom-4 left-4 inline-flex items-center gap-1.5 border border-emerald-300/40 bg-[#051630]/80 text-emerald-200 px-3 py-1.5 text-[11px] tracking-[0.15em] uppercase backdrop-blur">
                <ShieldCheck size={12} strokeWidth={1.5} /> Magnitude 9,5
              </span>
            </div>
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-emerald-200/80 mb-5">{c.wall.eyebrow}</p>
              <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-tight mb-6">
                {c.wall.title}
              </h2>
              <p className="text-white/70 font-light leading-relaxed text-lg mb-10">{c.wall.text}</p>

              <div className="space-y-4 mb-10">
                {c.wall.layers.map((l, i) => (
                  <div key={i} className="flex gap-4" data-testid={`produktion-wall-layer-${i}`}>
                    <span className="font-display text-lg text-white/30 w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <p className="font-display text-lg tracking-tight text-white">{l.name}</p>
                      <p className="text-sm text-white/60 font-light leading-relaxed">{l.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                {c.wall.benefits.map((b, i) => {
                  const BIcon = b.icon;
                  return (
                    <div key={i} className="flex items-start gap-3">
                      <BIcon size={18} strokeWidth={1.5} className="text-emerald-300 mt-0.5 shrink-0" />
                      <span className="text-sm text-white/75 leading-snug">{b.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Facade gallery */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28" data-testid="produktion-facades-section">
        <p className="text-xs tracking-[0.3em] uppercase text-emerald-200/80 mb-5">{c.facades.eyebrow}</p>
        <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-tight max-w-3xl mb-6">
          {c.facades.title}
        </h2>
        <p className="text-white/70 font-light leading-relaxed text-lg max-w-3xl mb-12">{c.facades.text}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {c.facades.items.map((name, i) => (
            <figure key={i} className="group" data-testid={`produktion-facade-${i}`}>
              <div className="relative overflow-hidden aspect-square border border-white/10">
                <img
                  src={FACADE_IMAGES[i]}
                  alt={name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <figcaption className="mt-4 flex items-center gap-2.5">
                <span className="font-display text-lg text-white/30">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-sm tracking-[0.12em] uppercase text-white/80">{name}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* Videos */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28" data-testid="produktion-videos-section">
        <div className="flex items-center gap-3 mb-5">
          <Play size={18} strokeWidth={1.5} className="text-emerald-300" />
          <p className="text-xs tracking-[0.3em] uppercase text-white/50">{c.videos.eyebrow}</p>
        </div>
        <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-tight max-w-3xl mb-6">
          {c.videos.title}
        </h2>
        <p className="text-white/70 font-light leading-relaxed text-lg max-w-3xl mb-12">{c.videos.text}</p>

        {/* Featured Royal House film */}
        <div className="mb-8 md:mb-10" data-testid="produktion-featured-video">
          <div className="relative w-full aspect-video overflow-hidden border border-white/10 bg-black">
            <iframe
              className="absolute inset-0 w-full h-full"
              src={`https://www.youtube-nocookie.com/embed/${FEATURED_VIDEO_ID}?rel=0&modestbranding=1&playsinline=1`}
              title="Royal House Bauträger"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {VIDEO_IDS.map((id, i) => (
            <div key={id} data-testid={`produktion-video-${i}`}>
              <div className="relative w-full aspect-video overflow-hidden border border-white/10 bg-black">
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1&playsinline=1`}
                  title="Royal House Bauträger"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10 bg-[#0A2244]/40">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28 flex flex-col items-start">
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-tight max-w-2xl mb-6">
            {c.cta.title}
          </h2>
          <p className="text-white/70 font-light leading-relaxed text-lg max-w-2xl mb-10">{c.cta.text}</p>
          <Link
            to="/kontakt"
            data-testid="produktion-cta-btn"
            className="inline-flex items-center gap-3 bg-white text-[#051630] font-bold tracking-[0.18em] uppercase text-xs px-8 py-4 border border-white hover:bg-transparent hover:text-white transition-colors"
          >
            <span>{c.cta.button}</span>
            <ArrowRight size={16} strokeWidth={1.75} />
          </Link>
        </div>
      </section>
    </div>
  );
}
